import React from "react";

/**
 * DataTable — one consistent table shell used by every page.
 *
 * columns: [{ key, header, cell?(row, index), className?, headerClassName?, hideBelow?: 'sm'|'md'|'lg'|'xl' }]
 *
 * - Desktop: real <table>, sticky-friendly header, subtle row hover.
 * - Mobile (<= md): renders each row as a stacked card using the column
 *   headers as labels, so wide tables never look like a spreadsheet.
 *
 * Purely presentational — `renderMobile` lets a page opt into a custom card.
 */
const HIDE_BELOW = {
    sm: "hidden sm:table-cell",
    md: "hidden md:table-cell",
    lg: "hidden lg:table-cell",
    xl: "hidden xl:table-cell",
};

export default function DataTable({
    columns,
    rows,
    rowKey = (_, i) => i,
    loading = false,
    skeleton,
    empty,
    onRowClick,
    mobileRender,
    className = "",
}) {
    if (loading) return skeleton || null;

    if (!rows || rows.length === 0) return empty || null;

    return (
        <div className={className}>
            {/* ---------------- Desktop / tablet: real table ---------------- */}
            <div className="hidden md:block overflow-x-auto scrollbar-slim">
                <table className="min-w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b border-slate-200/80 dark:border-slate-800">
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    scope="col"
                                    className={`whitespace-nowrap px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400
                                        ${col.hideBelow ? HIDE_BELOW[col.hideBelow] : ""} ${col.headerClassName || ""}`}
                                >
                                    {col.header}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {rows.map((row, i) => (
                            <tr
                                key={rowKey(row, i)}
                                onClick={onRowClick ? () => onRowClick(row, i) : undefined}
                                className={`transition-colors ${onRowClick ? "cursor-pointer" : ""} hover:bg-slate-50/80 dark:hover:bg-slate-800/40`}
                            >
                                {columns.map((col) => (
                                    <td
                                        key={col.key}
                                        className={`px-4 py-3.5 align-middle text-slate-700 dark:text-slate-300
                                            ${col.hideBelow ? HIDE_BELOW[col.hideBelow] : ""} ${col.className || ""}`}
                                    >
                                        {col.cell ? col.cell(row, i) : row[col.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* ---------------- Mobile: stacked cards ---------------- */}
            <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
                {rows.map((row, i) => (
                    <div key={rowKey(row, i)} className="px-4 py-4">
                        {mobileRender ? (
                            mobileRender(row, i)
                        ) : (
                            <div className="space-y-2.5">
                                {columns.map((col) => (
                                    <div key={col.key} className="flex items-start justify-between gap-4">
                                        <span className="flex-shrink-0 text-xs font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
                                            {col.header}
                                        </span>
                                        <span className="min-w-0 text-right text-sm text-slate-700 dark:text-slate-200">
                                            {col.cell ? col.cell(row, i) : row[col.key]}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
